// Drives prev/next navigation for the verification cockpit.
//
// The cockpit is normally opened from the Smart inbox; we want the user to
// be able to flip through their pending queue without bouncing back to the
// inbox between every document. This hook fetches a generous window of
// recent docs (cached, so it costs nothing if the inbox already loaded one
// shortly before) and returns the immediate neighbours of the current id
// along with their pre-built verify URLs.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { ocrApi } from "../../services/ocrApi";
import type { DocumentRow } from "../../types/documents";
import { buildVerifyHref } from "../utils/verifyHref";

const NAVIGATOR_PAGE_SIZE = 100;

export interface NavigatorTarget {
  doc: DocumentRow;
  href: string;
}

export interface UseDocumentNavigatorResult {
  prev: NavigatorTarget | null;
  next: NavigatorTarget | null;
  position: { index: number; total: number } | null;
  isLoading: boolean;
}

/**
 * Look up the document immediately before/after `processId` in the inbox
 * (sorted by start time, newest first — the same order the inbox uses).
 *
 * Returns null neighbors if the doc isn't found in the loaded window — we
 * don't try to seek further into the corpus because users typically work
 * through a recent batch, not the entire history.
 *
 * `tableName` chooses the source inbox: `config_studio_tracking` reads
 * from `/config_studio/homepage` (Template Engine), anything else uses
 * the default `/tracking_page` (OCR Tracking). The neighbour URLs we
 * return carry the same `table_name` forward so prev/next navigation
 * stays in the right context.
 */
export function useDocumentNavigator(
  processId: string,
  tableName?: string,
): UseDocumentNavigatorResult {
  const isConfigStudio = tableName === "config_studio_tracking";
  // Use the same query key prefix as SmartInbox so the cache is shared.
  // The `dataSource` segment ("configStudio" | "tracking") matches the
  // SmartInbox key shape, but the page-options object differs — that's
  // fine; react-query still hits the cache when keys are equal.
  const query = useQuery({
    queryKey: [
      "ocr",
      "tracking",
      isConfigStudio ? "configStudio" : "tracking",
      "navigator",
      { pageNumber: 1, recordsCount: NAVIGATOR_PAGE_SIZE },
    ],
    queryFn: () =>
      isConfigStudio
        ? ocrApi.configStudioHomepage({
            pageNumber: 1,
            recordsCount: NAVIGATOR_PAGE_SIZE,
          })
        : ocrApi.trackingPage({
            pageNumber: 1,
            recordsCount: NAVIGATOR_PAGE_SIZE,
          }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return useMemo<UseDocumentNavigatorResult>(() => {
    const rows = query.data?.rows ?? [];
    if (!processId || rows.length === 0) {
      return { prev: null, next: null, position: null, isLoading: query.isLoading };
    }
    const idx = rows.findIndex((r) => r.process_id === processId);
    if (idx === -1) {
      return { prev: null, next: null, position: null, isLoading: query.isLoading };
    }
    const prevDoc = idx > 0 ? rows[idx - 1] : null;
    const nextDoc = idx < rows.length - 1 ? rows[idx + 1] : null;
    const opts = tableName ? { tableName } : undefined;
    return {
      prev: prevDoc ? { doc: prevDoc, href: buildVerifyHref(prevDoc, opts) } : null,
      next: nextDoc ? { doc: nextDoc, href: buildVerifyHref(nextDoc, opts) } : null,
      position: { index: idx + 1, total: rows.length },
      isLoading: query.isLoading,
    };
  }, [query.data, query.isLoading, processId, tableName]);
}
